<template>
  <LoginFormContainer>
    <template #title>Verify your email address</template>

    <template #description>
      {{
        authStore.hasContext
          ? "Continue to complete the login process and start the registation"
          : "Continue to complete the login process"
      }}
    </template>

    <template #form>
      <v-text-field :model-value="tokenAuthDto?.email" :disabled="true" label="Email address" variant="solo" />
      <v-text-field :model-value="tokenAuthDto?.token" :disabled="true" label="Token" variant="solo" />
      <PPrimaryButton class="mt-1 continue-btn px-8" :loading="isLoading" :color="PwcTheme.colors.darkGreyButton"
        :disabled="isLoading" @click="submit">
        Continue
      </PPrimaryButton>
    </template>
  </LoginFormContainer>
</template>

<script setup lang="ts">
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import type { TokenAuthDto } from "@/models/dto/tokenAuthDto";
import { onBeforeMount, ref, type Ref } from "vue";
import { useRoute } from "vue-router";
import LoginFormContainer from "@/views/login-view/LoginFormContainer.vue";
import { PwcTheme } from "@/plugins/pwc-theme";
import { authenticate, contextuallyAuthenticate } from "@/services/authService";
import { useAuthenticationStore } from "@/stores/authentication";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { useRouter } from "vue-router";
import { RouterPath } from "@/models/enum/routerPath";
import type { AxiosResponse, AxiosError } from "axios";

const authStore = useAuthenticationStore();
const snackbarStore = useSnackBarStore();
const route = useRoute();
const router = useRouter();
const isLoading = ref(false);
const tokenAuthDto: Ref<TokenAuthDto> = ref({ email: "", token: "" });

onBeforeMount(() => {
  tokenAuthDto.value = {
    email: route.query.email as string,
    token: route.query.id as string,
  };
});

const submit = async (): Promise<void> => {
  try {
    const resp = authStore.hasContext
      ? await contextuallyAuthenticate({
        ...tokenAuthDto.value,
        context: authStore.context,
      })
      : await authenticate(tokenAuthDto.value);

    await handleAuthenticationResult(resp);
  } catch (error) {
    handleAuthenticationError(error as AxiosError);
  }
};

const handleAuthenticationResult = async (response: AxiosResponse): Promise<void> => {
  if (response.status === 200) {
    authStore.setAuthenticated(true);
    await authStore.getAuthenticationStatus();
    router.push(RouterPath.Home);
  } else {
    snackbarStore.showError("Authentication failed");
    authStore.setAuthenticated(false);
    router.push(RouterPath.Login);
  }
};

const handleAuthenticationError = (error: AxiosError): void => {
  authStore.setAuthenticated(false);

  if (error.response && error.response.status === 410) {
    router.push(RouterPath.LoginTokenExpired);
  } else {
    snackbarStore.showError("Authentication failed");
    router.push(RouterPath.Login);
  }
};
</script>

<style scoped lang="scss">
.continue-btn {
  width: fit-content;
  float: right;
}
</style>
